/**
 * What a `festival_kind` looks like and is called, in ONE place.
 *
 * `festivals.kind` is a single column with two values rather than two tables (decision 0010):
 * "en venue og en festival er forskjellige i hva de kalles og hvor ofte de gjentar seg, ikke i
 * hva systemet gjør med dem". The consequence is that the whole distinction is a rendering
 * concern — and it is rendered in three places at once: the sidebar groups by it, the sidebar
 * row picks its icon from it, and the festival page badges it. Three copies of a two-entry
 * lookup is three chances for the venue to be a festival on one of them.
 *
 * `Record<FestivalKind, …>` rather than a lookup with a fallback: adding a third kind to the
 * enum is then a compile error here, in the file that would have silently drawn it as a
 * festival.
 */
import type { FestivalKind } from '@tvitvi/shared';

import type { IconName } from '@/components/ui/icons';
import type { TKey } from '@/i18n/types';

/** The word for one of them — a badge, a Select option. */
export const FESTIVAL_KIND_LABEL: Readonly<Record<FestivalKind, TKey>> = {
  festival: 'enums.festival_kind.festival',
  venue: 'enums.festival_kind.venue',
};

/** The word for a group of them, which is the sidebar's heading and is therefore plural. */
export const FESTIVAL_KIND_GROUP_LABEL: Readonly<Record<FestivalKind, TKey>> = {
  festival: 'nav.festivals.group.festival',
  venue: 'nav.festivals.group.venue',
};

/**
 * The glyph. It is the only thing separating the two kinds on a sidebar row, which is why it may
 * not be a colour (DESIGN.md §3) and why it is registered in `ICONS` rather than imported here.
 */
export const FESTIVAL_KIND_ICON: Readonly<Record<FestivalKind, IconName>> = {
  festival: 'festival',
  venue: 'venue',
};
